<template>
  <div class="relative w-full pt-6">
    <input
      v-model="value"
      :type="type"
      :name="name"
      :aria-label="placeholder"
      :inputmode="type"
      :autocomplete="autocomplete"
      class="peer w-full flex outline-none pb-3 bg-transparent h-9"
      :class="inputColorClass"
      @keydown="checkInputValidity"
    />
    <span
      :class="spanClass"
      class="flex absolute w-full h-0.5 left-0 right-0 bottom-0 duration-500 after:content-[''] after:flex after:absolute after:w-0 after:h-0.5 after:left-auto after:right-0 after:bottom-0 after:duration-500 after:peer-focus:w-full after:peer-focus:left-0 z-20 after:peer-focus:right-auto"
    ></span>
    <label
      class="absolute left-0 top-6 text-base transition-all duration-200 pointer-events-none peer-placeholder-shown:top-6 peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-sm overflow-hidden whitespace-nowrap text-ellipsis max-w-full font-medium"
      :class="labelClass"
    >
      {{ placeholder }}
    </label>
  </div>
</template>

<script setup>
import DOMPurify from "dompurify";
const props = defineProps({
  modelValue: { type: String },
  placeholder: { type: String },
  name: { type: String },
  type: { type: String },
  autocomplete: { type: String },
  color: { type: String, default: "white" },
  error: { type: Boolean },
});

const emit = defineEmits(["update:modelValue"]);

const value = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const spanClass = computed(() => {
  if (props.error) return "bg-red-500 after:bg-red-600";
  if (props.color === "white") return "bg-white/30 after:bg-white";
  if (props.color === "black") return "bg-gray-200 after:bg-[#1a1a1a]";
  return "bg-brand-dark";
});

const labelClass = computed(() => {
  let baseClass = "";
  let activeClass = "";
  if (props.error) {
    baseClass = "text-red-500";
    activeClass = "peer-focus:text-red-600 peer-focus:font-semibold";
  } else if (props.color === "white") {
    baseClass = "text-white/60";
    activeClass = "peer-focus:text-white peer-focus:font-semibold";
  } else {
    baseClass = "text-[#71717a]";
    activeClass = "peer-focus:text-[#1a1a1a] peer-focus:font-semibold";
  }
  
  const isFilled = value.value && value.value.length > 0;
  return [
    baseClass, 
    activeClass,
    isFilled ? "!text-sm !top-1 font-semibold" : "",
    isFilled && props.color === "white" && !props.error ? "!text-white" : "",
    isFilled && props.color === "black" && !props.error ? "!text-[#1a1a1a]" : ""
  ].join(" ");
});

// Sanitize edilmiş değer
watch(
  () => props.modelValue,
  (val) => {
    const sanitized = DOMPurify.sanitize(val, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
    if (sanitized !== val) {
      emit("update:modelValue", sanitized);
    }
  }
);

const checkInputValidity = (e) => {
  const type = props.type;
  const key = e.key;

  // Bu tuşlara her durumda izin ver
  const controlKeys = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "Tab",
    "Enter",
    "Home",
    "End",
  ];

  if (controlKeys.includes(key)) return;

  // Sayı karakterleri kontrolü
  if ((type === "number" || type === "tel") && !/^[0-9+\-]$/.test(key)) {
    e.preventDefault();
  }
};
const inputColorClass = computed(() => ({
  'text-white font-semibold': props.color === 'white',
  'text-[#1a1a1a] font-semibold': props.color === 'black'
}));
</script>
